/**
 * @file components/chat/MessageBubble.tsx
 * Bulle texte classique. Verte/foncée (primary) côté utilisateur, claire côté agent.
 * 7.3-01 — Les bulles agent rendent le Markdown (sanitisé) ; les bulles
 * utilisateur restent en texte brut (whitespace-pre-wrap).
 * 7.3-02 — Barre d'actions contextuelle (copier / réessayer) : survol en
 * desktop, appui long en mobile, accessible au clavier (focus-within).
 * 7.3-08 — Bouton "Écouter" (TTS) sur les bulles agent, avec pause/stop et
 * vitesse réglable persistée.
 */
import React, { useCallback, useRef, useState } from 'react';
import { Bot, Check, Copy, Pause, Play, RotateCcw, ShieldAlert, Square, Volume2 } from 'lucide-react';
import StatusTicks from './StatusTicks.tsx';
import MarkdownText from './MarkdownText.tsx';
import MessageUsageBadge from './MessageUsageBadge.tsx';
import AgentDebugPanel from './AgentDebugPanel.tsx';
import { useTextToSpeech } from '../../hooks/useTextToSpeech.ts';
import type { TextMessage } from './types.ts';

function formatTime(at: number): string {
  return new Date(at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
}

const LONG_PRESS_MS = 500;
const FEEDBACK_MS = 1500;

type CopyFeedback = 'ok' | 'err' | null;

/**
 * Barre d'actions flottante (au-dessus de la bulle → aucun décalage de mise
 * en page). Visible au survol/focus clavier, ou si `forced` (appui long mobile).
 */
function ActionBar({
  message,
  alignRight,
  forced,
  onRetry,
}: {
  message: TextMessage;
  alignRight: boolean;
  forced: boolean;
  onRetry?: ((m: TextMessage) => void) | undefined;
}): React.ReactElement {
  const [copied, setCopied] = useState<CopyFeedback>(null);
  const feedbackTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copy = useCallback(() => {
    const done = (state: CopyFeedback) => {
      setCopied(state);
      if (feedbackTimer.current) clearTimeout(feedbackTimer.current);
      feedbackTimer.current = setTimeout(() => setCopied(null), FEEDBACK_MS);
    };
    // Dégradation gracieuse : clipboard absent (contexte non sécurisé…).
    if (!navigator.clipboard?.writeText) {
      done('err');
      return;
    }
    navigator.clipboard
      .writeText(message.text)
      .then(() => done('ok'))
      .catch(() => done('err'));
  }, [message.text]);

  const isUser = message.author === 'user';
  const isError = message.tone === 'info' || message.tone === 'security';
  const showRetry = onRetry !== undefined && (isUser || isError);

  // 7.3-08 : lecture à voix haute (bulles agent uniquement).
  const tts = useTextToSpeech('fr-FR');
  const showTts = !isUser && tts.supported && tts.enabled;

  return (
    <div
      role="toolbar"
      aria-label="Actions du message"
      className={`absolute -top-3.5 z-10 flex items-center gap-0.5 rounded-full border border-border bg-card px-1 py-0.5 shadow-sm transition-opacity ${
        alignRight ? 'right-2' : 'left-2'
      } ${forced ? 'opacity-100' : 'opacity-0 focus-within:opacity-100 group-hover:opacity-100'}`}
    >
      <button
        type="button"
        aria-label="Copier le message"
        title="Copier"
        onClick={copy}
        className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring"
      >
        {copied === 'ok' ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
      </button>
      {showRetry && (
        <button
          type="button"
          aria-label={isUser ? 'Renvoyer ce message' : 'Réessayer la dernière requête'}
          title="Réessayer"
          onClick={() => onRetry(message)}
          className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <RotateCcw size={13} />
        </button>
      )}
      {showTts && !tts.speaking && (
        <button
          type="button"
          aria-label="Écouter la réponse"
          title="Écouter"
          onClick={() => tts.speak(message.text)}
          className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Volume2 size={13} />
        </button>
      )}
      {showTts && tts.speaking && (
        <>
          <button
            type="button"
            aria-label={tts.paused ? 'Reprendre la lecture' : 'Mettre la lecture en pause'}
            title={tts.paused ? 'Reprendre' : 'Pause'}
            onClick={() => (tts.paused ? tts.resume() : tts.pause())}
            className="rounded-full p-1.5 text-primary transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:ring-ring"
          >
            {tts.paused ? <Play size={13} /> : <Pause size={13} />}
          </button>
          <button
            type="button"
            aria-label="Arrêter la lecture"
            title="Stop"
            onClick={tts.stop}
            className="rounded-full p-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Square size={13} />
          </button>
        </>
      )}
      {showTts && (
        <button
          type="button"
          aria-label={`Vitesse de lecture : ${tts.rate}. Changer.`}
          title="Vitesse de lecture"
          onClick={tts.cycleRate}
          className="rounded-full px-1.5 py-1 text-[10px] font-semibold text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:ring-2 focus-visible:ring-ring"
        >
          {tts.rate}×
        </button>
      )}
      {copied !== null && (
        <span role="status" className="px-1 text-[10px] text-muted-foreground">
          {copied === 'ok' ? 'Copié !' : 'Copie impossible'}
        </span>
      )}
    </div>
  );
}

export default function MessageBubble({
  message,
  onRetry,
  showAvatar = true,
  showMeta = true,
}: {
  message: TextMessage;
  /** 7.3-02 — câblé par la page : renvoi (bulle user) ou retry (bulle erreur). */
  onRetry?: ((m: TextMessage) => void) | undefined;
  /** 7.3-03 — avatar agent affiché uniquement sur la dernière bulle d'un groupe. */
  showAvatar?: boolean;
  /** 7.3-03 — heure/ticks affichés uniquement sur la dernière bulle d'un groupe. */
  showMeta?: boolean;
}): React.ReactElement {
  const isUser = message.author === 'user';

  // Appui long (mobile) → force l'affichage de la barre d'actions.
  const [pressed, setPressed] = useState(false);
  const pressTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const onTouchStart = useCallback(() => {
    pressTimer.current = setTimeout(() => setPressed((p) => !p), LONG_PRESS_MS);
  }, []);
  const cancelPress = useCallback(() => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  }, []);

  if (isUser) {
    return (
      <div className="flex justify-end">
        <div
          className="group relative max-w-[78%]"
          onTouchStart={onTouchStart}
          onTouchEnd={cancelPress}
          onTouchMove={cancelPress}
        >
          <ActionBar message={message} alignRight forced={pressed} onRetry={onRetry} />
          <div className="rounded-2xl rounded-br-sm bg-primary px-4 py-2.5 text-primary-foreground shadow-sm">
            <p className="whitespace-pre-wrap break-words text-sm leading-relaxed">{message.text}</p>
            {showMeta && (
              <div className="mt-1 flex items-center justify-end gap-1">
                <span className="text-[10px] text-primary-foreground/60">{formatTime(message.at)}</span>
                <StatusTicks status={message.tick} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  const toneClass =
    message.tone === 'security'
      ? 'bg-red-100 border-red-300 text-red-900 dark:bg-red-950/40 dark:border-red-800 dark:text-red-100'
      : message.tone === 'info'
        ? 'bg-sky-50 border-sky-200 text-sky-900 dark:bg-sky-950/40 dark:border-sky-800 dark:text-sky-100'
        : 'bg-card border-border text-card-foreground';

  const isSecurity = message.tone === 'security';

  return (
    <div className="flex items-end gap-2">
      {showAvatar ? (
        <div
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
            isSecurity ? 'bg-red-100 text-red-600 dark:bg-red-900/60 dark:text-red-300'
                       : 'bg-muted text-muted-foreground'
          }`}
        >
          {isSecurity ? <ShieldAlert size={15} /> : <Bot size={15} />}
        </div>
      ) : (
        <div aria-hidden className="h-7 w-7 shrink-0" />
      )}
      <div
        className="group relative max-w-[78%]"
        onTouchStart={onTouchStart}
        onTouchEnd={cancelPress}
        onTouchMove={cancelPress}
      >
        <ActionBar message={message} alignRight={false} forced={pressed} onRetry={onRetry} />
        <div
          role={isSecurity ? 'alert' : undefined}
          className={`rounded-2xl rounded-bl-sm border px-4 py-2.5 shadow-sm ${toneClass}`}
        >
          <MarkdownText text={message.text} />
          {showMeta && (
            <span className="mt-1 block text-[10px] text-muted-foreground">{formatTime(message.at)}</span>
          )}
        </div>
        {/* CHAT-07 — Badge FinOps sous la bulle agent */}
        {message.usage && <MessageUsageBadge usage={message.usage} />}
        {/* CHAT-06b — Timeline debug (admin, contrôlée par VITE_SHOW_AGENT_DEBUG) */}
        {message.statusDebugLog && message.statusDebugLog.length > 0 && (
          <AgentDebugPanel log={message.statusDebugLog} />
        )}
      </div>
    </div>
  );
}
